/**
 * Table Manager Module
 * Handles attribute table display, infinite scroll, and row interactions
 */

// DOM References
const tableHead = document.getElementById('table-head');
const tableBody = document.getElementById('table-body');
const tableRecordCount = document.getElementById('table-record-count');
const attributeTableContainer = document.getElementById('attribute-table-container');
const mapContainer = document.getElementById('map-container');
const showTableBtn = document.getElementById('show-table-btn');
const tableSearch = document.getElementById('table-search');
const tableScroll = document.querySelector('.table-scroll');

// State
let currentPropertyKeys = [];
let loadedRowCount = 0;
let currentFeatures = []; // Original unfiltered features
let filteredFeatures = []; // Currently filtered features
let currentOnRowClick = null; // Store the row click callback
const ROWS_PER_BATCH = 50;

// Sort state
let currentSortColumn = null;
let currentSortDirection = 'asc'; // 'asc' or 'desc'

// Field types metadata
let fieldTypes = {}; // Maps field name to type ('text', 'number', 'date')

/**
 * Create a table row for a feature
 * @param {Object} feature - The GeoJSON feature
 * @param {number} index - The feature index
 * @param {Array} propertyKeys - The property keys to display
 * @param {Function} onRowClick - Callback when row is clicked
 * @returns {HTMLElement} The table row element
 */
function createTableRow(feature, index, propertyKeys, onRowClick) {
    const properties = feature.properties || {};

    const row = document.createElement('tr');
    row.dataset.featureIndex = index;
    row.style.cursor = 'pointer';

    // Index column (not editable)
    const indexCell = document.createElement('td');
    indexCell.textContent = index + 1;
    indexCell.style.fontWeight = '500';
    indexCell.style.color = '#7f8c8d';
    indexCell.classList.add('index-cell');
    row.appendChild(indexCell);

    // Property columns (editable)
    propertyKeys.forEach(key => {
        const td = document.createElement('td');
        const value = properties[key];
        td.dataset.propertyKey = key;
        td.classList.add('editable-cell');

        // Check if value is an image data URL (thumbnail)
        if (typeof value === 'string' && value.startsWith('data:image/')) {
            // Create image element for thumbnails
            const img = document.createElement('img');
            img.src = value;
            img.style.maxWidth = '100px';
            img.style.maxHeight = '100px';
            img.style.objectFit = 'cover';
            img.style.borderRadius = '4px';
            img.style.cursor = 'pointer';
            img.title = 'Click to view full size';

            // Click to view full size
            img.addEventListener('click', (e) => {
                e.stopPropagation();
                const modal = document.createElement('div');
                modal.style.position = 'fixed';
                modal.style.top = '0';
                modal.style.left = '0';
                modal.style.width = '100%';
                modal.style.height = '100%';
                modal.style.background = 'rgba(0, 0, 0, 0.9)';
                modal.style.zIndex = '200000';
                modal.style.display = 'flex';
                modal.style.alignItems = 'center';
                modal.style.justifyContent = 'center';
                modal.style.cursor = 'pointer';

                const fullImg = document.createElement('img');
                fullImg.src = value;
                fullImg.style.maxWidth = '90%';
                fullImg.style.maxHeight = '90%';
                fullImg.style.objectFit = 'contain';
                fullImg.style.borderRadius = '8px';

                modal.appendChild(fullImg);
                modal.addEventListener('click', () => modal.remove());
                document.body.appendChild(modal);
            });

            td.appendChild(img);
            td.classList.add('image-cell');
            td.setAttribute('title', 'Click image to view full size');
        } else {
            // Regular text value
            td.textContent = value !== null && value !== undefined ? value : '';
            td.setAttribute('title', 'Double-click to edit');

            // Double-click to edit (only for non-image cells)
            td.addEventListener('dblclick', (e) => {
                e.stopPropagation();
                startCellEdit(td, index, key);
            });
        }

        row.appendChild(td);
    });

    // Add click handler (only for row selection, not editing)
    row.addEventListener('click', (e) => {
        // Don't trigger row click if clicking on an input
        if (e.target.tagName === 'INPUT') return;

        // If clicking the same row that's already highlighted, clear it
        if (row.classList.contains('highlighted')) {
            clearRowHighlight();
            // Also clear map highlight
            document.dispatchEvent(new CustomEvent('clearHighlight'));
        } else if (onRowClick) {
            onRowClick(index);
        }
    });

    return row;
}

/**
 * Start editing a cell
 * @param {HTMLElement} cell - The table cell to edit
 * @param {number} featureIndex - The feature index
 * @param {string} propertyKey - The property key being edited
 */
function startCellEdit(cell, featureIndex, propertyKey) {
    // Don't edit if already editing
    if (cell.querySelector('input')) return;

    const currentValue = cell.textContent;
    const fieldType = fieldTypes[propertyKey] || 'text';
    const input = document.createElement('input');

    // Set input type based on field type
    if (fieldType === 'number') {
        input.type = 'number';
        input.step = 'any'; // Allow decimals
    } else if (fieldType === 'date') {
        input.type = 'date';
        // Convert display value to YYYY-MM-DD format if needed
        if (currentValue && !currentValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
            const date = new Date(currentValue);
            if (!isNaN(date.getTime())) {
                input.value = date.toISOString().split('T')[0];
            }
        } else {
            input.value = currentValue;
        }
    } else {
        input.type = 'text';
        input.value = currentValue;
    }

    if (fieldType !== 'date') {
        input.value = currentValue;
    }

    input.classList.add('cell-edit-input');

    // Save on Enter or blur
    const saveEdit = () => {
        const newValue = input.value;

        // Validate based on field type
        if (!validateFieldValue(newValue, fieldType, propertyKey)) {
            // Validation failed, keep editing
            input.focus();
            return;
        }

        // Process value based on type
        let processedValue = newValue;
        if (fieldType === 'number' && newValue !== '') {
            processedValue = Number(newValue);
        }

        saveCellEdit(featureIndex, propertyKey, processedValue);
        cell.textContent = processedValue !== null && processedValue !== undefined ? processedValue : '';
        cell.classList.remove('editing');
    };

    // Cancel on Escape
    const cancelEdit = () => {
        cell.textContent = currentValue;
        cell.classList.remove('editing');
    };

    input.addEventListener('blur', saveEdit);
    input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            saveEdit();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            cancelEdit();
        }
    });

    // Replace cell content with input
    cell.textContent = '';
    cell.classList.add('editing');
    cell.appendChild(input);
    input.focus();
    input.select();
}

/**
 * Validate field value based on type
 * @param {string} value - The value to validate
 * @param {string} fieldType - The field type ('text', 'number', 'date')
 * @param {string} fieldName - The field name (for error messages)
 * @returns {boolean} True if valid
 */
function validateFieldValue(value, fieldType, fieldName) {
    // Empty values are allowed
    if (value === null || value === undefined || value === '') {
        return true;
    }

    if (fieldType === 'number') {
        const numValue = Number(value);
        if (isNaN(numValue)) {
            alert(`Invalid number for field "${fieldName}". Please enter a valid number.`);
            return false;
        }
    } else if (fieldType === 'date') {
        // HTML5 date input already validates format
        // Additional validation can be added here if needed
        if (value && !value.match(/^\d{4}-\d{2}-\d{2}$/)) {
            alert(`Invalid date format for field "${fieldName}". Please use YYYY-MM-DD format.`);
            return false;
        }
    }

    return true;
}

/**
 * Save edited cell value back to feature data
 * @param {number} featureIndex - The feature index
 * @param {string} propertyKey - The property key
 * @param {string} newValue - The new value
 */
function saveCellEdit(featureIndex, propertyKey, newValue) {
    console.log('[TABLE] Saving cell edit:', { featureIndex, propertyKey, newValue });

    // Update the original feature
    if (currentFeatures[featureIndex]) {
        currentFeatures[featureIndex].properties[propertyKey] = newValue;
        console.log('[TABLE] Updated currentFeatures');
    } else {
        console.error('[TABLE] Feature not found at index:', featureIndex);
        return;
    }

    // Update filtered features if needed
    const filteredFeature = filteredFeatures.find(f => currentFeatures.indexOf(f) === featureIndex);
    if (filteredFeature) {
        filteredFeature.properties[propertyKey] = newValue;
        console.log('[TABLE] Updated filteredFeatures');
    }

    // Trigger update event (will be handled by layerManager)
    const event = new CustomEvent('featureDataUpdated', {
        detail: { featureIndex, propertyKey, newValue }
    });
    document.dispatchEvent(event);
    console.log('[TABLE] Dispatched featureDataUpdated event');
}

/**
 * Load more rows into the table
 * @param {number} count - Number of rows to load
 */
function loadMoreTableRows(count = ROWS_PER_BATCH) {
    if (!filteredFeatures || filteredFeatures.length === 0) return;

    const startIndex = loadedRowCount;
    const endIndex = Math.min(startIndex + count, filteredFeatures.length);

    // Create and append rows
    const fragment = document.createDocumentFragment();
    for (let i = startIndex; i < endIndex; i++) {
        const feature = filteredFeatures[i];
        // Use the original index from currentFeatures for the row click
        const originalIndex = currentFeatures.indexOf(feature);
        const row = createTableRow(feature, originalIndex, currentPropertyKeys, currentOnRowClick);
        fragment.appendChild(row);
    }
    tableBody.appendChild(fragment);

    loadedRowCount = endIndex;

    // Update record count
    updateRecordCount();
}

/**
 * Filter features based on search term
 * @param {string} searchTerm - The search term
 */
function filterFeatures(searchTerm) {
    const term = searchTerm.toLowerCase().trim();

    if (!term) {
        // Show all features if search is empty
        filteredFeatures = [...currentFeatures];
    } else {
        // Filter features based on search term
        filteredFeatures = currentFeatures.filter(feature => {
            const properties = feature.properties || {};

            // Search across all property values
            return currentPropertyKeys.some(key => {
                const value = properties[key];
                if (value === null || value === undefined) return false;
                return String(value).toLowerCase().includes(term);
            });
        });
    }

    // Re-render table with filtered features
    tableBody.innerHTML = '';
    loadedRowCount = 0;
    loadMoreTableRows(ROWS_PER_BATCH);
}

/**
 * Sort the features by a column
 * @param {string} column - The column name to sort by
 */
function sortByColumn(column) {
    // Toggle sort direction if clicking the same column
    if (currentSortColumn === column) {
        currentSortDirection = currentSortDirection === 'asc' ? 'desc' : 'asc';
    } else {
        currentSortColumn = column;
        currentSortDirection = 'asc';
    }

    // Sort the filtered features array
    filteredFeatures.sort((a, b) => {
        const aValue = a.properties[column];
        const bValue = b.properties[column];

        // Handle null/undefined values
        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return currentSortDirection === 'asc' ? 1 : -1;
        if (bValue == null) return currentSortDirection === 'asc' ? -1 : 1;

        // Determine if values are numeric
        const aNum = Number(aValue);
        const bNum = Number(bValue);
        const isNumeric = !isNaN(aNum) && !isNaN(bNum);

        let comparison = 0;
        if (isNumeric) {
            // Numeric comparison
            comparison = aNum - bNum;
        } else {
            // String comparison (case-insensitive)
            const aStr = String(aValue).toLowerCase();
            const bStr = String(bValue).toLowerCase();
            comparison = aStr.localeCompare(bStr);
        }

        return currentSortDirection === 'asc' ? comparison : -comparison;
    });

    // Re-render the table from the beginning
    tableBody.innerHTML = '';
    loadedRowCount = 0;
    loadMoreTableRows(ROWS_PER_BATCH);

    // Update header to show active sort
    updateSortIndicators();
}

/**
 * Update sort indicator arrows in the table header
 */
function updateSortIndicators() {
    const headers = tableHead.querySelectorAll('th.sortable');
    headers.forEach(th => {
        const column = th.dataset.column;
        const ascIcon = th.querySelector('.sort-asc');
        const descIcon = th.querySelector('.sort-desc');

        if (column === currentSortColumn) {
            if (currentSortDirection === 'asc') {
                ascIcon?.classList.add('active');
                descIcon?.classList.remove('active');
            } else {
                ascIcon?.classList.remove('active');
                descIcon?.classList.add('active');
            }
        } else {
            ascIcon?.classList.remove('active');
            descIcon?.classList.remove('active');
        }
    });
}

/**
 * Update record count display
 */
function updateRecordCount() {
    const totalFeatures = currentFeatures.length;
    const filteredCount = filteredFeatures.length;

    if (filteredCount < totalFeatures) {
        // Filtered view
        tableRecordCount.textContent = `${filteredCount.toLocaleString()} features (filtered)`;
    } else {
        // Normal view
        tableRecordCount.textContent = `${totalFeatures.toLocaleString()} features`;
    }
}

/**
 * Display attribute table for a GeoJSON
 * @param {Object} geojson - The GeoJSON data
 * @param {Function} onRowClick - Callback when a row is clicked
 * @param {Object} metadata - Layer metadata (optional, contains field types)
 */
export function displayAttributeTable(geojson, onRowClick, metadata = null) {
    const features = geojson.features || [];
    if (features.length === 0) return;

    // Store current features and callback
    currentFeatures = features;
    filteredFeatures = [...features]; // Initialize with all features
    currentOnRowClick = onRowClick;

    // Get property keys from first feature (filter out internal properties)
    const firstFeature = features[0];
    const propertyKeys = Object.keys(firstFeature.properties || {})
        .filter(key => !key.startsWith('__')); // Exclude internal properties like __featureIndex
    currentPropertyKeys = propertyKeys;

    // Initialize field types from metadata or default to 'text'
    fieldTypes = {};
    if (metadata && metadata.fieldTypes) {
        fieldTypes = { ...metadata.fieldTypes };
    } else {
        // Default all fields to 'text' type
        propertyKeys.forEach(key => {
            fieldTypes[key] = 'text';
        });
    }

    if (propertyKeys.length === 0) {
        attributeTableContainer.style.display = 'none';
        return;
    }

    // Create table header with sort functionality
    const headerRow = document.createElement('tr');

    // Index column (not sortable)
    const indexHeader = document.createElement('th');
    indexHeader.textContent = '#';
    indexHeader.classList.add('non-sortable');
    headerRow.appendChild(indexHeader);

    // Property columns (sortable)
    propertyKeys.forEach(key => {
        const th = document.createElement('th');
        th.classList.add('sortable');
        th.dataset.column = key;

        const headerContent = document.createElement('div');
        headerContent.classList.add('th-content');

        const label = document.createElement('span');
        label.textContent = key;

        const sortIcons = document.createElement('span');
        sortIcons.classList.add('sort-icons');
        sortIcons.innerHTML = `
            <svg class="sort-icon sort-asc ${currentSortColumn === key && currentSortDirection === 'asc' ? 'active' : ''}" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M7 14l5-5 5 5z"/>
            </svg>
            <svg class="sort-icon sort-desc ${currentSortColumn === key && currentSortDirection === 'desc' ? 'active' : ''}" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M7 10l5 5 5-5z"/>
            </svg>
        `;

        headerContent.appendChild(label);
        headerContent.appendChild(sortIcons);
        th.appendChild(headerContent);

        // Add click handler for sorting
        th.addEventListener('click', () => {
            sortByColumn(key);
        });

        headerRow.appendChild(th);
    });

    // Add "+" button column
    const addFieldHeader = document.createElement('th');
    addFieldHeader.classList.add('add-field-header');
    addFieldHeader.innerHTML = `
        <button class="add-field-btn" title="Add new field">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <path d="M12 5v14M5 12h14"></path>
            </svg>
        </button>
    `;
    addFieldHeader.querySelector('.add-field-btn').addEventListener('click', (e) => {
        showAddFieldPopout(e.target.closest('.add-field-btn'));
    });
    headerRow.appendChild(addFieldHeader);

    tableHead.innerHTML = '';
    tableHead.appendChild(headerRow);

    // Clear table body and search
    tableBody.innerHTML = '';
    loadedRowCount = 0;
    tableSearch.value = '';

    // Load ALL rows immediately for simplicity and guaranteed scrolling
    // Modern browsers handle thousands of DOM rows fine
    loadMoreTableRows(filteredFeatures.length);

    // Show table (hide by default on mobile)
    const isMobile = window.innerWidth <= 768;
    if (!isMobile) {
        showTable();
    } else {
        // On mobile, keep table hidden but show the button to open it
        showTableBtn.style.display = 'block';
    }
}

/**
 * Handle scroll event for infinite loading
 * @param {HTMLElement} container - The scroll container element
 */
export function createScrollHandler(container) {
    return function handleTableScroll() {
        const scrollPosition = container.scrollTop + container.clientHeight;
        const scrollHeight = container.scrollHeight;

        // Load more rows when user scrolls to within 400px of bottom (more responsive)
        // Or if there are fewer than 200 total rows, just load all of them
        if ((scrollHeight - scrollPosition < 400 || filteredFeatures.length <= 200) && loadedRowCount < filteredFeatures.length) {
            loadMoreTableRows();
        }
    };
}

/**
 * Show the attribute table
 */
export function showTable() {
    attributeTableContainer.classList.remove('hidden');
    attributeTableContainer.classList.add('visible');
    mapContainer.classList.add('table-visible');
    showTableBtn.style.display = 'none';

    // Resize map to 2/3 height
    setTimeout(() => {
        if (window.resizeMap) {
            window.resizeMap();
        }
    }, 350);
}

/**
 * Hide the attribute table
 */
export function hideTable() {
    attributeTableContainer.classList.add('hidden');
    attributeTableContainer.classList.remove('visible');
    mapContainer.classList.remove('table-visible');
    showTableBtn.style.display = 'block';

    // Resize map to full height
    setTimeout(() => {
        if (window.resizeMap) {
            window.resizeMap();
        }
    }, 50);
}

/**
 * Clear the table
 */
export function clearTable() {
    tableBody.innerHTML = '';
    tableHead.innerHTML = '';
    tableRecordCount.textContent = '';
    tableSearch.value = '';
    loadedRowCount = 0;
    currentPropertyKeys = [];
    currentFeatures = [];
    filteredFeatures = [];
    currentOnRowClick = null;
    currentSortColumn = null;
    currentSortDirection = 'asc';
    fieldTypes = {};

    // Hide the entire table container when cleared
    hideTable();
}

/**
 * Highlight a row in the table
 * @param {number} index - The feature index
 * @param {boolean} shouldScroll - Whether to scroll to the row (default: true)
 */
export function highlightRow(index, shouldScroll = true) {
    // Remove previous highlights
    tableBody.querySelectorAll('tr').forEach(row => {
        row.classList.remove('highlighted');
    });

    // Check if the row exists in the DOM
    let row = tableBody.querySelector(`tr[data-feature-index="${index}"]`);

    // If row doesn't exist, we need to load it (infinite scroll optimization)
    if (!row) {
        // Find the index of this feature in the filtered features array
        const featureIndexInFiltered = filteredFeatures.findIndex(f =>
            currentFeatures.indexOf(f) === index
        );

        if (featureIndexInFiltered !== -1) {
            // For large datasets, load a window of rows around the target feature
            // This is much more efficient than loading all rows up to it
            const WINDOW_SIZE = 200; // Load 200 rows around the target
            const startIdx = Math.max(0, featureIndexInFiltered - Math.floor(WINDOW_SIZE / 2));
            const endIdx = Math.min(filteredFeatures.length, startIdx + WINDOW_SIZE);

            // Clear table and load the window
            tableBody.innerHTML = '';
            loadedRowCount = 0;

            // Create a fragment for better performance
            const fragment = document.createDocumentFragment();
            for (let i = startIdx; i < endIdx; i++) {
                const feature = filteredFeatures[i];
                const originalIndex = currentFeatures.indexOf(feature);
                const rowElement = createTableRow(feature, originalIndex, currentPropertyKeys, currentOnRowClick);
                fragment.appendChild(rowElement);
            }
            tableBody.appendChild(fragment);
            loadedRowCount = endIdx;

            // Update scroll position to account for missing rows above
            if (tableScroll && startIdx > 0) {
                // Estimate row height and set scroll position
                const estimatedRowHeight = 40; // Approximate row height in pixels
                tableScroll.scrollTop = 0; // Reset scroll since we cleared the table
            }

            // Try to find the row again after loading
            row = tableBody.querySelector(`tr[data-feature-index="${index}"]`);
        }
    }

    // Highlight and optionally scroll to the row
    if (row) {
        row.classList.add('highlighted');

        // Only scroll if requested (e.g., when clicking on map, not when clicking table row)
        if (shouldScroll && tableScroll) {
            const rowTop = row.offsetTop;
            // Account for table header height + some padding
            // The header is sticky/fixed, so we need more offset
            const headerHeight = tableHead ? tableHead.offsetHeight : 40;
            const scrollOffset = headerHeight + 10; // Add 10px padding
            tableScroll.scrollTo({ top: rowTop - scrollOffset, behavior: 'smooth' });
        }
    }
}

/**
 * Clear all row highlights
 */
export function clearRowHighlight() {
    tableBody.querySelectorAll('tr').forEach(row => {
        row.classList.remove('highlighted');
    });
}

/**
 * Get current loaded row count
 */
export function getLoadedRowCount() {
    return loadedRowCount;
}

// Listen for clear highlight events
document.addEventListener('clearHighlight', () => {
    clearRowHighlight();
});

/**
 * Show the Add Field popout
 * @param {HTMLElement} triggerBtn - The button that triggered the popout
 */
function showAddFieldPopout(triggerBtn) {
    // Remove any existing popout
    const existingPopout = document.querySelector('.add-field-popout-container');
    if (existingPopout) {
        existingPopout.remove();
        return; // Toggle off if already open
    }

    // Create container with overlay
    const container = document.createElement('div');
    container.classList.add('add-field-popout-container');
    container.innerHTML = `
        <div class="popout-overlay"></div>
        <div class="add-field-popout">
            <div class="popout-header">
                <h4>Add New Field</h4>
                <button class="popout-close-btn">&times;</button>
            </div>
            <div class="popout-body">
                <div class="popout-success-message" style="display: none;">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M20 6L9 17l-5-5"></path>
                    </svg>
                    <span>Field added successfully!</span>
                </div>
                <div class="popout-form-group">
                    <label for="popout-field-name">Field Name</label>
                    <input type="text" id="popout-field-name" class="popout-input" placeholder="Name" maxlength="10" />
                    <small>Max 10 characters</small>
                </div>
                <div class="popout-form-group">
                    <label>Type</label>
                    <div class="popout-type-btns">
                        <button type="button" class="popout-type-btn active" data-type="text">Text</button>
                        <button type="button" class="popout-type-btn" data-type="number">#</button>
                        <button type="button" class="popout-type-btn" data-type="date">Date</button>
                    </div>
                </div>
                <div class="popout-form-group">
                    <label for="popout-default-value">Default Value</label>
                    <input type="text" id="popout-default-value" class="popout-input" placeholder="Optional" />
                </div>
                <div class="popout-actions">
                    <button type="button" class="popout-btn popout-btn-finished">Done</button>
                    <button type="button" class="popout-btn popout-btn-add">Add</button>
                </div>
            </div>
        </div>
    `;

    // Add to document
    document.body.appendChild(container);

    const popout = container.querySelector('.add-field-popout');
    const overlay = container.querySelector('.popout-overlay');

    // Get elements
    const fieldNameInput = popout.querySelector('#popout-field-name');
    const defaultValueInput = popout.querySelector('#popout-default-value');
    const typeBtns = popout.querySelectorAll('.popout-type-btn');
    const finishedBtn = popout.querySelector('.popout-btn-finished');
    const addBtn = popout.querySelector('.popout-btn-add');
    const closeBtn = popout.querySelector('.popout-close-btn');
    const successMessage = popout.querySelector('.popout-success-message');

    // Type button handlers
    typeBtns.forEach(btn => {
        btn.addEventListener('click', () => {
            typeBtns.forEach(b => b.classList.remove('active'));
            btn.classList.add('active');
        });
    });

    // Show success message
    const showSuccessMessage = (fieldName) => {
        const messageText = successMessage.querySelector('span');
        messageText.textContent = `Field "${fieldName}" added successfully!`;
        successMessage.style.display = 'flex';

        // Auto-hide after 3 seconds
        setTimeout(() => {
            successMessage.style.display = 'none';
        }, 3000);
    };

    // Reset form function
    const resetForm = () => {
        fieldNameInput.value = '';
        defaultValueInput.value = '';
        typeBtns.forEach(b => b.classList.remove('active'));
        typeBtns[0].classList.add('active'); // Set Text as default
        fieldNameInput.focus();
    };

    // Close popout function
    const closePopout = () => {
        container.remove();
    };

    // Close button
    closeBtn.addEventListener('click', closePopout);

    // Finished button
    finishedBtn.addEventListener('click', closePopout);

    // Click overlay to close
    overlay.addEventListener('click', closePopout);

    // Add button - resets form instead of closing
    addBtn.addEventListener('click', () => {
        const fieldName = fieldNameInput.value.trim();
        const fieldType = popout.querySelector('.popout-type-btn.active').dataset.type;
        const defaultValue = defaultValueInput.value.trim();

        // Validate field name
        if (!fieldName) {
            alert('Please enter a field name');
            fieldNameInput.focus();
            return;
        }

        if (fieldName.length > 10) {
            alert('Field name must be 10 characters or less');
            fieldNameInput.focus();
            return;
        }

        // Check if field already exists
        if (currentPropertyKeys.includes(fieldName)) {
            alert(`Field "${fieldName}" already exists`);
            fieldNameInput.focus();
            return;
        }

        // Add field to all features
        addFieldToFeatures(fieldName, fieldType, defaultValue);

        // Show success message
        showSuccessMessage(fieldName);

        // Reset form for adding another field
        resetForm();
    });

    // Enter key handler
    fieldNameInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            addBtn.click();
        }
    });

    // Focus the name input
    fieldNameInput.focus();
}

/**
 * Add a new field to all features
 * @param {string} fieldName - The name of the new field
 * @param {string} fieldType - The type of field (text, number, date)
 * @param {string} defaultValue - The default value for the field
 */
function addFieldToFeatures(fieldName, fieldType, defaultValue) {
    // Process default value based on type
    let processedDefaultValue = defaultValue || null;

    if (defaultValue) {
        if (fieldType === 'number') {
            const numValue = Number(defaultValue);
            if (isNaN(numValue)) {
                alert('Invalid number for default value');
                return;
            }
            processedDefaultValue = numValue;
        } else if (fieldType === 'date') {
            // Keep as string in ISO format
            processedDefaultValue = defaultValue;
        }
    }

    // Add the new field to all features
    currentFeatures.forEach(feature => {
        if (!feature.properties) {
            feature.properties = {};
        }
        feature.properties[fieldName] = processedDefaultValue;
    });

    // Update filtered features (they share the same objects)
    // No need to update separately since they reference the same feature objects

    // Add to property keys and field types
    currentPropertyKeys.push(fieldName);
    fieldTypes[fieldName] = fieldType;

    // Dispatch event to save changes to IndexedDB
    const event = new CustomEvent('layerDataUpdated', {
        detail: {
            action: 'fieldAdded',
            fieldName,
            fieldType,
            defaultValue: processedDefaultValue,
            fieldTypes: { ...fieldTypes } // Send updated field types
        }
    });
    document.dispatchEvent(event);

    // Refresh the table to show the new column
    const headerRow = tableHead.querySelector('tr');
    if (headerRow) {
        // Find the add field header and insert new column before it
        const addFieldHeader = headerRow.querySelector('.add-field-header');

        const th = document.createElement('th');
        th.classList.add('sortable');
        th.dataset.column = fieldName;

        const headerContent = document.createElement('div');
        headerContent.classList.add('th-content');

        const label = document.createElement('span');
        label.textContent = fieldName;

        const sortIcons = document.createElement('span');
        sortIcons.classList.add('sort-icons');
        sortIcons.innerHTML = `
            <svg class="sort-icon sort-asc" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M7 14l5-5 5 5z"/>
            </svg>
            <svg class="sort-icon sort-desc" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M7 10l5 5 5-5z"/>
            </svg>
        `;

        headerContent.appendChild(label);
        headerContent.appendChild(sortIcons);
        th.appendChild(headerContent);

        // Add click handler for sorting
        th.addEventListener('click', () => {
            sortByColumn(fieldName);
        });

        headerRow.insertBefore(th, addFieldHeader);
    }

    // Add cells to all existing rows
    const rows = tableBody.querySelectorAll('tr');
    rows.forEach(row => {
        const featureIndex = parseInt(row.dataset.featureIndex);
        const feature = currentFeatures[featureIndex];

        const td = document.createElement('td');
        const value = feature.properties[fieldName];
        td.dataset.propertyKey = fieldName;
        td.classList.add('editable-cell');
        td.textContent = value !== null && value !== undefined ? value : '';
        td.setAttribute('title', 'Double-click to edit');

        // Double-click to edit
        td.addEventListener('dblclick', (e) => {
            e.stopPropagation();
            startCellEdit(td, featureIndex, fieldName);
        });

        // Insert before the last cell (which would be empty due to add field header)
        // Actually, we need to append it before the position where add-field-header would be
        const addFieldHeaderIndex = currentPropertyKeys.length; // This is now the new field's position
        row.appendChild(td);
    });

    console.log(`[TABLE] Added new field "${fieldName}" (${fieldType}) with default value:`, processedDefaultValue);
}

// Initialize search functionality
tableSearch.addEventListener('input', (e) => {
    filterFeatures(e.target.value);
});
